import { useState, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronLeft, ChevronRight, Search, Eye, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { copyId, truncateIdFirstLast } from "../lib/utils";
import { useAllProperties } from "@/hooks/queryHooks";
import { CopyIndicator } from "./CopyIndicator";
import { PropertyDetailsModal } from "./modals/PropertyDetailsModal";
import { Property } from "global-types";

type SortDirection = "asc" | "desc" | null;

const PropertiesList: React.FC = () => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchType, setSearchType] = useState<"name" | "identifier" | "account" | "accountId">("name");
    const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);
    const { data, isPending, error } = useAllProperties();

    const properties = data?.properties || [];

    const filteredProperties = useMemo(() => {
        let filtered = properties;

        if (searchTerm.trim()) {
            filtered = properties.filter(property => {
                const searchLower = searchTerm.trim().toLowerCase();
                switch (searchType) {
                    case "name":
                        return property.property_name?.toLowerCase().includes(searchLower);
                    case "identifier":
                        return property.property_identifier?.toLowerCase().includes(searchLower);
                    case "account":
                        return property.account_name?.toLowerCase().includes(searchLower);
                    case "accountId":
                        return property.account_id?.toLowerCase().includes(searchLower);
                    default:
                        return false;
                }
            });
        }

        if (sortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const nameA = (a.property_name || "").toLowerCase();
                const nameB = (b.property_name || "").toLowerCase();

                if (sortDirection === "asc") {
                    return nameA.localeCompare(nameB);
                } else {
                    return nameB.localeCompare(nameA);
                }
            });
        }

        return filtered;
    }, [properties, searchTerm, sortDirection]);

    const totalProperties = filteredProperties.length;
    const totalPages = Math.ceil(totalProperties / pageSize);

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentProperties = filteredProperties.slice(startIndex, endIndex);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize: string) => {
        const size = parseInt(newPageSize);
        setPageSize(size);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleSearchTypeChange = (value: "name" | "identifier" | "account" | "accountId") => {
        setSearchType(value);
        setSearchTerm(""); // Reset search term when changing search type
        setCurrentPage(1); // Reset to first page when changing search type
    };

    const handleViewDetails = (property: Property) => {
        setSelectedProperty(property);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedProperty(null);
    };

    const handleSortByName = () => {
        if (sortDirection === null) {
            setSortDirection("asc");
        } else if (sortDirection === "asc") {
            setSortDirection("desc");
        } else {
            setSortDirection(null);
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    const getSortIcon = () => {
        if (sortDirection === "asc") {
            return <ArrowUp className="h-4 w-4" />;
        } else if (sortDirection === "desc") {
            return <ArrowDown className="h-4 w-4" />;
        }
        return <ArrowUpDown className="h-4 w-4" />;
    };

    if (isPending) {
        return <div className="p-4">Loading properties...</div>;
    }

    if (error) {
        return (
            <div className="p-4 text-red-600">
                Error loading properties: {error.message}
            </div>
        );
    }

    return (
        <>
            {/* Search and Page Size Controls */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                    <div className="relative flex items-center">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 z-10" />
                        <Input
                            type="text"
                            placeholder={`Search by ${searchType === "name" ? "property name" : searchType === "accountId" ? "account ID" : searchType}...`}
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="pl-10 pr-40 w-96"
                        />
                        <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
                            <Select value={searchType} onValueChange={handleSearchTypeChange}>
                                <SelectTrigger className="w-36 h-8 border-0 bg-transparent focus:ring-0 cursor-pointer">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="name">Property Name</SelectItem>
                                    <SelectItem value="identifier">Property Identifier</SelectItem>
                                    <SelectItem value="account">Account Name</SelectItem>
                                    <SelectItem value="accountId">Account ID</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <div className="text-sm text-gray-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalProperties)} of {totalProperties} properties
                </div>
            </div>

            {/* Properties Table Container */}
            <div className="overflow-x-auto shadow w-full rounded-xs">
                {currentProperties.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">
                        {searchTerm ? `No properties found matching "${searchTerm}"` : "No properties found."}
                    </p>
                ) : (
                    <Table className="w-full">
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>
                                    <Button
                                        variant="ghost"
                                        onClick={handleSortByName}
                                        className="h-auto p-0 font-semibold hover:bg-transparent cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            Name
                                            {getSortIcon()}
                                        </div>
                                    </Button>
                                </TableHead>
                                <TableHead>Property Identifier</TableHead>
                                <TableHead>Account Name</TableHead>
                                <TableHead>Property Management System</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currentProperties.map((property) => (
                                <TableRow key={property.id}>
                                    <TableCell>
                                        <button
                                            onClick={() => {
                                                setActiveId(property.id);
                                                copyId(property.id);
                                            }}
                                            className="hover:text-blue-600 transition-colors cursor-pointer inline-flex items-center gap-2"
                                            title="Click to copy ID"
                                        >
                                            {property.id ? truncateIdFirstLast(property.id) : "-"}
                                            <CopyIndicator activeId={activeId} thisId={property.id} />
                                        </button>
                                    </TableCell>
                                    <TableCell>{property.property_name || "-"}</TableCell>
                                    <TableCell>{property.property_identifier || "-"}</TableCell>
                                    <TableCell>{property.account_name || "-"}</TableCell>
                                    <TableCell className="capitalize">{property.property_management_system || "-"}</TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleViewDetails(property)}
                                            className="flex items-center gap-2 cursor-pointer"
                                        >
                                            <Eye className="h-4 w-4" />
                                            Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Pagination Controls - Outside Table */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Properties per page:</span>
                        <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                            <SelectTrigger className="w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="20">20</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="cursor-pointer"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                        </Button>

                        <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                const shouldShow =
                                    page === 1 ||
                                    page === totalPages ||
                                    Math.abs(page - currentPage) <= 1;

                                if (shouldShow) {
                                    return (
                                        <Button
                                            key={page}
                                            variant={page === currentPage ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => handlePageChange(page)}
                                            className="w-8 h-8 p-0 cursor-pointer"
                                        >
                                            {page}
                                        </Button>
                                    );
                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return <span key={page} className="px-2">...</span>;
                                }
                                return null;
                            })}
                        </div>

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="cursor-pointer"
                        >
                            Next
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Property Details Modal */}
            <PropertyDetailsModal
                property={selectedProperty}
                open={isDetailsModalOpen}
                onClose={handleCloseDetailsModal}
            />
        </>
    );
};

export default PropertiesList; 
